import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ColumnSelect } from "@/components/ui/select";
import { Calendar, FileSpreadsheet, Loader2, AlertCircle, Check, Clock, Users, Plus, Link2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { DynamicConfigStepProps } from '../types';
import { AppointmentRemindersConfig } from './AppointmentRemindersConfig'; // ✅ Import added
import { api } from "@/contexts/AuthContext";
import GoogleSheetsPicker from "@/components/GoogleSheetsPicker";
import { connectGoogleSheet, createGoogleSheet, getConnectedSheets, getGoogleSheetsAuthStatus, getGoogleSheetsAuthorizationUrl, getGoogleSheetTabs, SheetTab } from "@/lib/googleSheetsApi";


const EMPTY_EXCLUDED_TAB_NAMES: string[] = [];
const EMPTY_EXCLUDED_TAB_IDS: number[] = [];

// Componente para seleccionar Hoja y Columnas para Campañas Dinámicas
// Usa exactamente los mismos colores y estilos que StepDataMarketing
export const DynamicConfigStep: React.FC<DynamicConfigStepProps> = ({
    config,
    onConfigChange,
    onNext,
    onBack,
    campaignName,
    excludedTabNames = EMPTY_EXCLUDED_TAB_NAMES,
    excludedTabIds = EMPTY_EXCLUDED_TAB_IDS
}) => {

    const [sheets, setSheets] = useState<any[]>([]);
    const [loadingSheets, setLoadingSheets] = useState(true);
    const [sheetTabs, setSheetTabs] = useState<SheetTab[]>([]);
    const [loadingSheetTabs, setLoadingSheetTabs] = useState(false);
    const [columns, setColumns] = useState<string[]>([]);
    const [loadingColumns, setLoadingColumns] = useState(false);
    const [showTutorialModal, setShowTutorialModal] = useState(false);
    const [showCreateSheetModal, setShowCreateSheetModal] = useState(false);
    const [newSheetName, setNewSheetName] = useState("");
    const [creatingSheet, setCreatingSheet] = useState(false);
    const [pickerAutoOpen, setPickerAutoOpen] = useState(false);
    const currentSheetIdRef = React.useRef(config.google_sheet_id);
    currentSheetIdRef.current = config.google_sheet_id;

    const refreshTabsRequestRef = React.useRef(0);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    // Cargar hojas al montar
    useEffect(() => {
        const loadSheets = async () => {
            setLoadingSheets(true);
            try {
                const connectedSheets = await getConnectedSheets();
                const sortedSheets = (connectedSheets || []).sort((a: any, b: any) => {
                    const dateA = new Date(a.last_modified || a.updated_at || a.created_at);
                    const dateB = new Date(b.last_modified || b.updated_at || b.created_at);
                    return dateB.getTime() - dateA.getTime();
                });
                setSheets(sortedSheets);
            } catch (error) {
                console.error("Error loading sheets:", error);
                toast.error("Error al cargar las hojas de Google Sheets");
            } finally {
                setLoadingSheets(false);
            }
        };
        loadSheets();
    }, []);

    // Cargar pestañas internas cuando se selecciona una hoja
    useEffect(() => {
        if (!config.google_sheet_id) {
            setSheetTabs([]);
            return;
        }

        let cancelled = false;

        const loadSheetTabs = async () => {
            try {
                setLoadingSheetTabs(true);

                const tabs = await getGoogleSheetTabs(config.google_sheet_id);
                const visibleTabs = tabs.filter(
                    (tab) =>
                        !tab.hidden &&
                        !excludedTabIds.includes(tab.sheet_id) &&
                        !excludedTabNames.includes(tab.title)
                );

                if (!cancelled) {
                    setSheetTabs(visibleTabs);
                }
            } catch (error) {
                console.error("Error cargando pestañas de Google Sheets:", error);

                if (!cancelled) {
                    setSheetTabs([]);
                }
            } finally {
                if (!cancelled) {
                    setLoadingSheetTabs(false);
                }
            }
        };

        void loadSheetTabs();

        return () => {
            cancelled = true;
        };
    }, [config.google_sheet_id, excludedTabNames, excludedTabIds]);

    // Cargar columnas cuando se selecciona una pestaña
    useEffect(() => {
        if (!config.google_sheet_id || !config.google_sheet_tab_name) {
            setColumns([]);
            return;
        }

        let cancelled = false;

        const loadColumns = async () => {
            setLoadingColumns(true);

            try {
                const response = await api.get(
                    `/google-sheets/${config.google_sheet_id}/data`,
                    {
                        params: {
                            max_rows: 1,
                            sheet_name: config.google_sheet_tab_name,
                            google_sheet_tab_id: config.google_sheet_tab_id,
                        },
                    }
                );

                if (!cancelled && response.data && response.data.columns) {
                    setColumns(response.data.columns);
                }
            } catch (error) {
                console.error("Error loading columns:", error);

                if (!cancelled) {
                    toast.error("Error al cargar las columnas de la pestaña");
                    setColumns([]);
                }
            } finally {
                if (!cancelled) {
                    setLoadingColumns(false);
                }
            }
        };

        void loadColumns();

        return () => {
            cancelled = true;
        };
    }, [config.google_sheet_id, config.google_sheet_tab_id, config.google_sheet_tab_name]);

    const handleSheetSelect = (sheetId: string) => {
        const selectedSheet = sheets.find((s: any) => (s.sheet_id || s.id) === sheetId);

        onConfigChange({
            ...config,
            google_sheet_id: sheetId,
            google_sheet_name: selectedSheet?.sheet_name || selectedSheet?.name,
            google_sheet_tab_id: null,
            google_sheet_tab_name: "",
            date_column: "",
            phone_column: "",
            time_column: ""
        });

        setColumns([]);
    };

    const handleTabSelect = (tabName: string) => {
        const selectedTab = sheetTabs.find((tab) => tab.title === tabName);

        onConfigChange({
            ...config,
            google_sheet_tab_id: selectedTab?.sheet_id ?? null,
            google_sheet_tab_name: tabName,
            date_column: "",
            phone_column: "",
            time_column: ""
        });

        setColumns([]);
    };

    
    const refreshTabsOnOpen = async () => {
        const sheetId = config.google_sheet_id;
        if (!sheetId) return;

        const requestId = ++refreshTabsRequestRef.current;

        try {
            const tabs = await getGoogleSheetTabs(sheetId);

            if (
                currentSheetIdRef.current !== sheetId ||
                refreshTabsRequestRef.current !== requestId
            ) {
                return;
            }

            const visibleTabs = tabs.filter(
                (tab) =>
                    !tab.hidden &&
                    !excludedTabIds.includes(tab.sheet_id) &&
                    !excludedTabNames.includes(tab.title)
            );

            setSheetTabs(visibleTabs);
        } catch (error) {
            if (
                currentSheetIdRef.current !== sheetId ||
                refreshTabsRequestRef.current !== requestId
            ) {
                return;
            }

            console.error("Error actualizando pestañas:", error);
            toast.error("No se pudieron actualizar las pestañas de Google Sheets");
        }
    };


    const createNewSheet = async (): Promise<void> => {
        const trimmedName = newSheetName.trim();

        if (!trimmedName) return;

        setCreatingSheet(true);

        try {
            const data = await createGoogleSheet(trimmedName);

            const newSheetId = String(
                data?.sheet_id ??
                data?.id ??
                data?.spreadsheet_id ??
                ''
            );

            // Recargar listado de hojas
            const connectedSheets = await getConnectedSheets({ force: true });

            const sortedSheets = (connectedSheets || []).sort((a: any, b: any) => {
                const dateA = new Date(a.last_modified || a.updated_at || a.created_at);
                const dateB = new Date(b.last_modified || b.updated_at || b.created_at);
                return dateB.getTime() - dateA.getTime();
            });

            setSheets(sortedSheets);

            // Seleccionar automáticamente la hoja recién creada
            if (newSheetId) {
                const createdSheet = sortedSheets.find((sheet: any) =>
                    String(sheet.sheet_id ?? sheet.id ?? sheet._id ?? '') === newSheetId
                );

                onConfigChange({
                ...config,
                google_sheet_id: newSheetId,
                google_sheet_name:
                    createdSheet?.name ??
                    trimmedName,
                google_sheet_tab_id: null,
                google_sheet_tab_name: "",
                date_column: "",
                phone_column: "",
                time_column: ""
            });

                setColumns([]);
            }

            setNewSheetName("");
            setShowCreateSheetModal(false);

            toast.success("Hoja de cálculo creada correctamente");
        } catch (error) {
            console.error("Error creando nueva hoja de cálculo:", error);
            toast.error("No se pudo crear la hoja de cálculo");
        } finally {
            setCreatingSheet(false);
        }
    };

    const handlePickerSheetsSelected = async (
        selectedSheets: Array<{ id: string }>
    ): Promise<void> => {
        if (selectedSheets.length === 0) {
            setPickerAutoOpen(false);
            return;
        }

        try {
            for (const sheet of selectedSheets) {
                try {
                    await connectGoogleSheet(sheet.id);
                } catch (error) {
                    console.error(`Error conectando hoja ${sheet.id}:`, error);
                }
            }

            const connectedSheets = await getConnectedSheets({ force: true });

            const sortedSheets = (connectedSheets || []).sort((a: any, b: any) => {
                const dateA = new Date(
                    a.last_modified || a.updated_at || a.created_at
                );

                const dateB = new Date(
                    b.last_modified || b.updated_at || b.created_at
                );

                return dateB.getTime() - dateA.getTime();
            });

            setSheets(sortedSheets);

            const selectedId =
                selectedSheets[selectedSheets.length - 1]?.id;

            if (selectedId) {
                const selectedSheet = sortedSheets.find((sheet: any) =>
                    String(
                        sheet.sheet_id ??
                        sheet.id ??
                        sheet._id ??
                        ''
                    ) === String(selectedId)
                );

                onConfigChange({
                    ...config,
                    google_sheet_id: selectedId,
                    google_sheet_name:
                        selectedSheet?.name ??
                        selectedId,
                    google_sheet_tab_id: null,
                    google_sheet_tab_name: "",
                    date_column: "",
                    phone_column: "",
                    time_column: ""
                });

                setColumns([]);
            }
        } catch (error) {
            console.error(
                'Error conectando hoja desde Google Picker:',
                error
            );

            toast.error(
                'No se pudo conectar la hoja seleccionada'
            );
        } finally {
            setPickerAutoOpen(false);
        }
    };

    const handleConnectSheets = async (fallbackToIntegrations = false): Promise<void> => {
        try {
            localStorage.setItem('renewal_create_campaign_type', 'Dynamic');

            if (campaignName) {
                localStorage.setItem('renewal_create_campaign_name', campaignName);
            }

            const authStatus = await getGoogleSheetsAuthStatus();

            if (authStatus.is_authenticated && !authStatus.needs_auth) {
                setPickerAutoOpen(true);
                return;
            }

            const authorizationUrl = await getGoogleSheetsAuthorizationUrl('dynamic-campaign');

            if (authorizationUrl) {
                window.location.href = authorizationUrl;
                return;
            }

            if (fallbackToIntegrations) {
                throw new Error('No authorization URL received');
            }
        } catch (error) {
            console.error(fallbackToIntegrations ? 'Error connecting to Sheets:' : 'Error:', error);

            if (fallbackToIntegrations) {
                window.location.href = '/integrations';
            }
        }
    };

    // 3. At least one reminder configuration (default OR custom days/hours) required.
    const isValid = config.google_sheet_id &&
        config.google_sheet_tab_name &&
        config.date_column &&
        config.phone_column &&
        config.time_column && (
            config.use_default_reminders ||
            (config.reminder_days_before && config.reminder_days_before.length > 0) ||
            (config.reminder_hours_before && config.reminder_hours_before.length > 0)
        );

    const hasTriggeredRef = React.useRef(false);

    // ✅ TOUR TRIGGER: Auto-start tour based on context and manual restarts
    useEffect(() => {
        if (loadingSheets) return;

        const tourKey = 'dynamic_tour_config_seen';

        const triggerAppropriateStage = () => {
            const hasSheets = sheets.length > 0;
            const hasSelectedSheet = !!config.google_sheet_id;
            const hasSelectedTab = !!config.google_sheet_tab_name;

            // Stage 1: Si aún no hay hoja seleccionada
            if (!hasSelectedSheet) {
                setTimeout(() => {
                    import('@/components/common/DynamicCampaignTour').then(({ startDynamicConfigTour }) => {
                        startDynamicConfigTour(hasSheets, () => {
                            if (hasSheets) localStorage.setItem(tourKey, 'true');
                        });
                    });
                }, 600);
            }
            // Stage 3: Hoja + pestaña + todas las columnas configuradas
            else if (
                hasSelectedTab &&
                config.date_column &&
                config.phone_column &&
                config.time_column
            ) {
                setTimeout(() => {
                    import('@/components/common/DynamicCampaignTour').then(({ startDynamicRemindersTour }) => {
                        startDynamicRemindersTour(() => { });
                    });
                }, 600);
            }
            // Stage 2: Hoja + pestaña seleccionadas y columnas disponibles
            else if (hasSelectedTab && columns.length > 0) {
                setTimeout(() => {
                    import('@/components/common/DynamicCampaignTour').then(({ startDynamicColumnsTour }) => {
                        startDynamicColumnsTour(() => { });
                    });
                }, 600);
            }
        };

        const hasSeen = localStorage.getItem(tourKey);
        if (!hasSeen && !hasTriggeredRef.current) {
            hasTriggeredRef.current = true;
            triggerAppropriateStage();
        }

        const handleRestart = (e: Event) => {
            if (e instanceof CustomEvent && e.detail && !e.detail.handled) {
                e.detail.handled = true;
                localStorage.removeItem(tourKey);
                hasTriggeredRef.current = true; // reset it as true after manual trigger too?
                triggerAppropriateStage();
            }
        };

        window.addEventListener('restart-local-tour', handleRestart);

        return () => {
            window.removeEventListener('restart-local-tour', handleRestart);
        };
    }, [loadingSheets, sheets.length, !!config.google_sheet_id, !!config.google_sheet_tab_name, columns.length, !!config.date_column, !!config.phone_column, !!config.time_column]);

    // Pagination calculations
    const total = sheets.length;
    const totalPages = Math.ceil(total / itemsPerPage);
    const start = (currentPage - 1) * itemsPerPage;
    const visibleSheets = sheets.slice(start, start + itemsPerPage);

    return (
        <Card className="mt-6">
            <CardContent className="space-y-6 p-6">
                {/* Header - Same styling as StepDataMarketing */}
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex-none bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white flex items-center justify-center">
                        <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-lg font-semibold text-gray-900">Configurar Campaña Dinámica</div>
                        <p className="text-xs text-gray-400">Conecta tu hoja y define qué fechas activarán los mensajes.</p>
                    </div>
                </div>

                {/* Preparar tu hoja - Guía paso a paso */}
                <div className="mt-6 relative rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
                    <div className="flex items-start gap-5 pl-4">
                        <div className="flex-1">
                            <h4 className="text-lg font-semibold text-gray-900 mb-1">Prepara tu hoja de cálculo</h4>
                            <p className="text-xs text-gray-600 leading-relaxed mb-4">
                                Antes de continuar, asegúrate de que tu hoja tenga las columnas de teléfono, fecha y hora configuradas correctamente.
                            </p>
                            <button
                                id="tour-dynamic-guide"
                                onClick={() => setShowTutorialModal(true)}
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-sm font-semibold text-white shadow-[0_4px_20px_rgba(28,179,179,0.4)] hover:shadow-[0_6px_25px_rgba(28,179,179,0.5)] transition-all animate-[pulse_2s_ease-in-out_infinite] hover:animate-none"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                                Ver guía paso a paso
                            </button>
                        </div>
                    </div>
                </div>

                {/* Sheet Selection Card - Same styling as StepDataMarketing */}
                <Card className="mt-4 group relative rounded-xl border border-gray-200 bg-white p-4 mb-4 shadow-[0_8px_30px_rgba(12,20,30,0.06)]">
                    <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
                    <CardContent className="pl-4">
                        <div className="mb-3">
                            <Label className="text-lg font-semibold mb-0">Selecciona tu hoja de cálculo</Label>
                            <p className="text-xs text-gray-500 mt-1">Elige la hoja de Google Sheets que contiene los datos y fechas de tu campaña dinámica</p>
                        </div>

                        <div className="mt-2">
                            {loadingSheets ? (
                                <div className="flex items-center gap-2 text-gray-500 py-4">
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    <span className="text-xs">Cargando hojas...</span>
                                </div>
                            ) : sheets.length === 0 ? (
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 bg-gradient-to-r from-[#e6f7f4] to-[#f0f9fa] rounded-full flex items-center justify-center mx-auto mb-4 border border-[#e0f2f1]">
                                        <FileSpreadsheet className="h-8 w-8 text-[#1CB3B3]" />
                                    </div>
                                    <h3 className="font-medium mb-2">No hay hojas conectadas</h3>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Primero debes conectar al menos una hoja de Google Sheets para continuar.
                                    </p>
                                    <Button
                                        id="tour-dynamic-connect"
                                        variant="outline"
                                        onClick={() => void handleConnectSheets(true)}
                                        className="bg-gradient-to-r from-[#1CB3B3] to-[#179999] hover:from-[#179999] hover:to-[#1CB3B3] text-white font-semibold px-6 h-11 rounded-xl shadow-lg hover:shadow-cyan-500/30 transform transition-all active:scale-95"
                                    >
                                        Conectar Google Sheets
                                    </Button>
                                </div>
                            ) : (
                                <div>
                                    <div id="tour-dynamic-sheets-grid" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        {visibleSheets.map((sheet: any, idx: number) => {
                                            const id = sheet.sheet_id ?? sheet.id ?? sheet._id ?? String(idx);
                                            const name = sheet.sheet_name || sheet.name || String(id);
                                            const lastModified = sheet.last_modified || sheet.updated_at || sheet.created_at;
                                            const modifiedDate = lastModified ? new Date(lastModified) : null;
                                            const isSelected = String(config.google_sheet_id) === String(id);

                                            return (
                                                <div
                                                    key={id}
                                                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all relative ${isSelected
                                                        ? "border-[#1CB3B3] bg-gradient-to-r from-[#1CB3B3]/10 to-[#179999]/10 ring-1 ring-[#1CB3B3]/20"
                                                        : "border-gray-200 hover:border-gray-300 hover:shadow-md"
                                                        }`}
                                                    onClick={() => handleSheetSelect(id)}
                                                >
                                                    <div className="flex justify-end mb-3 sm:absolute sm:right-3 sm:top-3 sm:mb-0">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const url = sheet.url || `https://docs.google.com/spreadsheets/d/${sheet.sheet_id || sheet.id}`;
                                                                window.open(url, "_blank", "noopener,noreferrer");
                                                            }}
                                                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-[#1CB3B3] text-[#0D7377] text-xs font-medium hover:bg-[#E8F7F7] transition-all"
                                                            title="Abrir hoja en Google Sheets"
                                                        >
                                                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" /></svg>
                                                            Abrir hoja
                                                        </button>
                                                    </div>
                                                    <h4 className="font-medium text-gray-900 mb-1 break-words sm:pr-28">{name}</h4>
                                                    <div className="flex flex-col gap-1">
                                                        {modifiedDate && (
                                                            <p className="text-xs text-gray-500 flex items-center gap-1">
                                                                <Clock className="h-3 w-3" /> Modificado: {modifiedDate.toLocaleDateString("es-ES", { year: "numeric", month: "short", day: "numeric" })}
                                                            </p>
                                                        )}
                                                        {(sheet.row_count !== undefined && sheet.row_count > 0) && (
                                                            <p className="text-xs text-gray-500 flex items-center gap-1">
                                                                <Users className="h-3 w-3" /> {sheet.row_count} contactos
                                                            </p>
                                                        )}
                                                    </div>
                                                    {isSelected && (
                                                        <div className="inline-flex items-center gap-1 mt-2 bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-white text-xs px-2 py-1 rounded shadow-sm">
                                                            <Check className="h-3 w-3" /> Seleccionada
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Pagination Controls */}
                                    {total > itemsPerPage && (
                                        <div className="flex items-center justify-between mt-4">
                                            <div className="text-sm text-gray-600">{Math.min(start + 1, total)}-{Math.min(start + visibleSheets.length, total)} de {total} hojas</div>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                                    disabled={currentPage === 1}
                                                >
                                                    ←
                                                </Button>
                                                <div className="px-3 py-1 rounded bg-gray-100 text-sm">{currentPage} / {totalPages}</div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                                    disabled={currentPage >= totalPages}
                                                >
                                                    →
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    {config.google_sheet_id && (
                                        <div className="mt-4 pt-3 border-t border-gray-100">
                                            <Label className="text-base font-semibold mb-0">
                                                Selecciona la pestaña
                                            </Label>

                                            <div className="text-xs text-gray-500 mt-1 mb-2">
                                                Elige la pestaña interna de Google Sheets que contiene los datos de esta campaña dinámica.
                                            </div>

                                            {loadingSheetTabs ? (
                                                <div className="text-xs text-gray-500">
                                                    Cargando pestañas...
                                                </div>
                                            ) : (
                                                <ColumnSelect
                                                    options={sheetTabs.map((tab) => tab.title)}
                                                    value={config.google_sheet_tab_name || ""}
                                                    onChange={(tabName) => handleTabSelect(tabName || "")}
                                                    onOpen={() => void refreshTabsOnOpen()}
                                                    placeholder="Seleccionar pestaña..."
                                                />
                                            )}

                                            {!loadingSheetTabs && sheetTabs.length === 0 && (
                                                <p className="text-xs text-amber-600 mt-2">
                                                    No se encontraron pestañas disponibles en esta hoja de cálculo.
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {/* Acciones de Google Sheets */}
                                    <div className="flex flex-col lg:flex-row justify-center gap-3 mt-6 pt-4 border-t border-gray-100">
                                        <Button
                                            onClick={() => void handleConnectSheets()}
                                            className="w-full lg:w-auto bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-6 h-11 rounded-xl shadow-lg flex items-center justify-center gap-2 font-semibold transition-all active:scale-95"
                                        >
                                            <Link2 className="w-4 h-4 mr-2" />
                                            Conectar Hojas
                                        </Button>

                                        <Button
                                            onClick={() => setShowCreateSheetModal(true)}
                                            disabled={creatingSheet}
                                            className="w-full lg:w-auto min-w-0 bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-4 lg:px-6 min-h-11 h-auto py-2.5 rounded-xl shadow-lg flex items-center justify-center gap-2 font-semibold transition-all active:scale-95 whitespace-normal text-center leading-tight"
                                        >
                                            <Plus className="w-4 h-4 shrink-0" />
                                            <span>Nueva Hoja de Cálculo</span>
                                        </Button>
                                    </div>

                                    {/* Column Selection - Same styling */}
                                    {config.google_sheet_id && config.google_sheet_tab_name && (
                                        <div id="tour-dynamic-columns" className="mt-6 pt-4 border-t border-gray-100">
                                            <div className="mb-3">
                                                <Label className="text-lg font-semibold mb-0">Asignar columnas</Label>
                                                <div className="text-xs text-gray-500">Selecciona las columnas de teléfono, fecha y hora para tu campaña dinámica</div>
                                            </div>

                                            {loadingColumns ? (
                                                <div className="flex items-center gap-2 text-gray-500 py-4">
                                                    <Loader2 className="w-5 h-5 animate-spin" />
                                                    <span className="text-xs">Cargando columnas...</span>
                                                </div>
                                            ) : columns.length === 0 ? (
                                                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex flex-col sm:flex-row items-start gap-3">
                                                    <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm font-medium text-amber-800">La hoja está vacía</div>
                                                        <div className="text-xs text-amber-600 mt-1">
                                                            Necesitas añadir datos a tu hoja de cálculo antes de continuar.
                                                            Asegúrate de incluir una columna con los números de <strong>teléfono</strong>, <strong>fecha</strong> y <strong>hora</strong>.
                                                        </div>
                                                        <div className="flex flex-col sm:flex-row gap-2 mt-3">
                                                            <button
                                                                type="button"
                                                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#1CB3B3] text-[#0D7377] text-sm font-medium hover:bg-[#E8F7F7] transition-all"
                                                                onClick={() => {
                                                                    const sheet = sheets.find((s: any) => (s.sheet_id || s.id) === config.google_sheet_id);
                                                                    const url = sheet?.url || `https://docs.google.com/spreadsheets/d/${config.google_sheet_id}`;
                                                                    window.open(url, "_blank", "noopener,noreferrer");
                                                                }}
                                                            >
                                                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" /></svg>
                                                                Abrir hoja
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-white text-sm font-medium hover:shadow-lg transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                                                                disabled={loadingColumns}
                                                                onClick={async () => {
                                                                    if (!config.google_sheet_id || !config.google_sheet_tab_name) return;

                                                                    setLoadingColumns(true);

                                                                    try {
                                                                        const response = await api.get(
                                                                            `/google-sheets/${config.google_sheet_id}/data`,
                                                                            {
                                                                                params: {
                                                                                    max_rows: 1,
                                                                                    sheet_name: config.google_sheet_tab_name,
                                                                                    google_sheet_tab_id: config.google_sheet_tab_id,
                                                                                },
                                                                            }
                                                                        );

                                                                        if (response.data && response.data.columns) {
                                                                            setColumns(response.data.columns);
                                                                            toast.success("Columnas actualizadas correctamente");
                                                                        }
                                                                    } catch (error) {
                                                                        console.error("Error updating columns:", error);
                                                                        toast.error("Error al actualizar las columnas");
                                                                    } finally {
                                                                        setLoadingColumns(false);
                                                                    }
                                                                }}
                                                            >
                                                                <RefreshCw
                                                                    className={`w-4 h-4 shrink-0 ${loadingColumns ? 'animate-spin' : ''}`}
                                                                />
                                                                {loadingColumns ? 'Actualizando...' : 'Actualizar columnas'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                        <div>
                                                            <ColumnSelect
                                                                label="Columna de Teléfono"
                                                                options={columns}
                                                                value={config.phone_column}
                                                                onChange={(val: string) => onConfigChange({ ...config, phone_column: val })}
                                                                placeholder="Seleccionar columna"
                                                            />
                                                            <p className="text-xs text-gray-500 mt-2 pl-1">
                                                                Los números de WhatsApp.
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <ColumnSelect
                                                                label="Columna de Fecha"
                                                                options={columns}
                                                                value={config.date_column}
                                                                onChange={(val: string) => onConfigChange({ ...config, date_column: val })}
                                                                placeholder="Seleccionar columna"
                                                            />
                                                            <p className="text-xs text-gray-500 mt-2 pl-1">
                                                                El día del evento o cita.
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <ColumnSelect
                                                                label="Hora de la Cita"
                                                                options={columns}
                                                                value={config.time_column || ""}
                                                                onChange={(val: string) => onConfigChange({ ...config, time_column: val || undefined })}
                                                                placeholder="Seleccionar columna"
                                                            />
                                                            <p className="text-xs text-gray-500 mt-2 pl-1">
                                                                Hora específica de la cita para programar recordatorios.
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Botón Actualizar columnas */}
                                                   <div className="mt-4 flex justify-center">
                                                        <button
                                                            type="button"
                                                            onClick={async () => {
                                                                if (!config.google_sheet_id || !config.google_sheet_tab_name) return;

                                                                setLoadingColumns(true);

                                                                try {
                                                                    const response = await api.get(
                                                                        `/google-sheets/${config.google_sheet_id}/data`,
                                                                        {
                                                                            params: {
                                                                                max_rows: 1,
                                                                                sheet_name: config.google_sheet_tab_name,
                                                                                google_sheet_tab_id: config.google_sheet_tab_id,
                                                                            },
                                                                        }
                                                                    );

                                                                    if (response.data && response.data.columns) {
                                                                        setColumns(response.data.columns);
                                                                        toast.success("Columnas actualizadas correctamente");
                                                                    }
                                                                } catch (error) {
                                                                    console.error("Error updating columns:", error);
                                                                    toast.error("Error al actualizar las columnas");
                                                                } finally {
                                                                    setLoadingColumns(false);
                                                                }
                                                            }}
                                                            disabled={loadingColumns}
                                                            className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-[#1CB3B3] text-[#0D7377] text-xs font-medium hover:bg-[#E8F7F7] transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                                                        >
                                                            <RefreshCw
                                                                className={`w-3.5 h-3.5 shrink-0 ${loadingColumns ? 'animate-spin' : ''}`}
                                                            />
                                                            {loadingColumns ? 'Actualizando...' : 'Actualizar columnas'}
                                                        </button>
                                                    </div>

                                                    {/* Explanatory note about appointment mode */}
                                                    <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                                                        <p className="text-xs text-blue-700">
                                                            <strong>💡 Nota:</strong> La fecha y hora seleccionadas arriba corresponden a la <strong>Cita</strong>. Define cuándo enviar los recordatorios.
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* ✅ SECCIÓN RECORDATORIOS (Siempre visible cuando hay columnas) */}
                {config.google_sheet_id && columns.length > 0 && config.time_column && (
                    <div id="tour-dynamic-reminders" className="mt-6">
                        <AppointmentRemindersConfig
                            useDefault={config.use_default_reminders || false}
                            setUseDefault={(val) => onConfigChange({ ...config, use_default_reminders: val })}
                            reminderDays={config.reminder_days_before || []}
                            setReminderDays={(days) => onConfigChange({ ...config, reminder_days_before: days })}
                            reminderHours={config.reminder_hours_before || []}
                            setReminderHours={(hours) => onConfigChange({ ...config, reminder_hours_before: hours })}
                        />
                    </div>
                )}
            </CardContent>

            {/* Footer - Exact same styling as StepDataMarketing */}
            <CardFooter className="pt-6 border-t border-gray-100">
                <div className="w-full flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <Button
                        variant="outline"
                        onClick={onBack}
                        className="w-full sm:w-auto border-gray-300 text-gray-600 hover:bg-gray-50 px-6 h-11 rounded-xl shadow-sm"
                    >
                        ← Atrás
                    </Button>
                    <Button
                        onClick={onNext}
                        disabled={!isValid}
                        className="w-full sm:w-auto bg-gradient-to-r from-[#1CB3B3] to-[#179999] hover:from-[#179999] hover:to-[#1CB3B3] text-white font-semibold px-8 h-11 rounded-xl shadow-lg hover:shadow-cyan-500/30 transform transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Siguiente →
                    </Button>
                </div>
            </CardFooter>

            {pickerAutoOpen && (
                <GoogleSheetsPicker
                    onSheetsSelected={handlePickerSheetsSelected}
                    onPickerClosed={() => setPickerAutoOpen(false)}
                    autoOpen={true}
                    className="hidden"
                />
            )}

            {/* Modal Nueva Hoja de Cálculo */}
            {showCreateSheetModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
                        <div className="mb-5">
                            <h2 className="text-xl font-bold text-gray-900">
                                Nueva Hoja de Cálculo
                            </h2>

                            <p className="mt-1 text-sm text-gray-500">
                                Ingresa el nombre de la nueva hoja de cálculo.
                            </p>
                        </div>

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                void createNewSheet();
                            }}
                        >
                            <div className="mb-6">
                                <label
                                    htmlFor="new-sheet-name"
                                    className="mb-2 block text-sm font-medium text-gray-700"
                                >
                                    Nombre de la hoja
                                </label>

                                <input
                                    id="new-sheet-name"
                                    type="text"
                                    value={newSheetName}
                                    onChange={(e) => setNewSheetName(e.target.value)}
                                    placeholder="Ej. Clientes octubre 2026"
                                    autoFocus
                                    disabled={creatingSheet}
                                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 outline-none transition focus:border-[#1CB3B3] focus:ring-2 focus:ring-[#1CB3B3]/20 disabled:bg-gray-100"
                                />
                            </div>

                            <div className="flex justify-end gap-3">
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={creatingSheet}
                                    onClick={() => {
                                        setNewSheetName("");
                                        setShowCreateSheetModal(false);
                                    }}
                                >
                                    Cancelar
                                </Button>

                                <Button
                                    type="submit"
                                    disabled={!newSheetName.trim() || creatingSheet}
                                    className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white"
                                >
                                    {creatingSheet ? "Creando..." : "Crear Hoja"}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Tutorial Modal */}
            {showTutorialModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => setShowTutorialModal(false)}>
                    <div className="relative bg-white rounded-xl shadow-2xl max-w-[95vw] w-full max-h-[95vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setShowTutorialModal(false)} aria-label="Cerrar guía" className="absolute top-3 right-3 z-20 bg-white/80 hover:bg-white rounded-full p-1 focus:outline-none">
                            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                        <div className="flex items-center justify-center" style={{ padding: 12 }}>
                            <img
                                src="/assets/marketing.png"
                                alt="Guía paso a paso - Campañas Dinámicas"
                                className="rounded-md shadow-md"
                                style={{ width: '100%', height: 'auto', maxHeight: 'calc(95vh - 24px)', objectFit: 'contain' }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
};
